import { useCallback, useEffect, useMemo, useState } from "react";
import { useSiteBranding } from "@/context/SiteContext";
import { api } from "@/lib/api";
import {
  SHORTLIST_UPDATED_EVENT,
  buildShortlistSharePath,
  clearShortlistItems,
  downloadShortlistCsv,
  getShortlistStorageKey,
  mergeShortlistItems,
  readShortlistItems,
  removeShortlistItem,
  toggleShortlistItem,
  writeShortlistItems,
} from "@/lib/shortlistStorage";

export const usePortalShortlist = () => {
  const { buildBrandedPath, isAgentPath, siteContext } = useSiteBranding();
  const scope = useMemo(() => (siteContext?.requested_slug && isAgentPath ? siteContext.requested_slug : "global"), [isAgentPath, siteContext?.requested_slug]);
  const [items, setItems] = useState(() => readShortlistItems(scope));

  useEffect(() => {
    setItems(readShortlistItems(scope));
  }, [scope]);

  useEffect(() => {
    const syncFromStorage = (event) => {
      if (event.detail?.scope && event.detail.scope !== scope) return;
      setItems(readShortlistItems(scope));
    };
    const syncAcrossTabs = (event) => {
      if (event.key && event.key !== getShortlistStorageKey(scope)) return;
      setItems(readShortlistItems(scope));
    };
    window.addEventListener(SHORTLIST_UPDATED_EVENT, syncFromStorage);
    window.addEventListener("storage", syncAcrossTabs);
    return () => {
      window.removeEventListener(SHORTLIST_UPDATED_EVENT, syncFromStorage);
      window.removeEventListener("storage", syncAcrossTabs);
    };
  }, [scope]);

  const ids = useMemo(() => items.map((item) => item.id).filter(Boolean), [items]);
  const sharePath = useMemo(() => buildShortlistSharePath(buildBrandedPath, ids), [buildBrandedPath, ids]);
  const shareUrl = useMemo(() => (typeof window === "undefined" ? sharePath : `${window.location.origin}${sharePath}`), [sharePath]);
  const supportsNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const trackShortlistEvent = useCallback(async (eventType, payload = {}) => {
    try {
      await api.post("/lead-intent", {
        event_type: eventType,
        listing_id: payload.listing_id,
        listing_title: payload.listing_title,
        metadata: {
          agent_slug: siteContext?.requested_slug,
          owner_id: payload.owner_id,
          team_id: payload.team_id,
          shortlist_size: payload.shortlist_size,
          listing_count: payload.listing_count,
          listing_ids: payload.listing_ids || [],
          listing_title: payload.listing_title,
          event_source: payload.event_source,
        },
      });
    } catch {
      // analytics should never block shortlist actions
    }
  }, [siteContext?.requested_slug]);

  const isShortlisted = useCallback((listingId) => ids.includes(listingId), [ids]);

  const toggleListing = useCallback((listing) => {
    const response = toggleShortlistItem(scope, listing);
    setItems(response.items);
    if (response.added) {
      void trackShortlistEvent("shortlist_saved", {
        event_source: "shortlist_button",
        listing_count: response.items.length,
        listing_id: listing.id,
        listing_title: listing.title,
        owner_id: listing.owner_id,
        team_id: listing.team_id,
        shortlist_size: response.items.length,
      });
    }
    return response.added;
  }, [scope, trackShortlistEvent]);

  const removeListing = useCallback((listingId) => {
    setItems(removeShortlistItem(scope, listingId));
  }, [scope]);

  const clearAll = useCallback(() => {
    setItems(clearShortlistItems(scope));
  }, [scope]);

  const mergeListings = useCallback((incomingListings = []) => {
    const mergedItems = mergeShortlistItems(readShortlistItems(scope), incomingListings);
    setItems(writeShortlistItems(scope, mergedItems));
  }, [scope]);

  const exportCsv = useCallback((fileLabel = "signature-shortlist") => {
    downloadShortlistCsv(items, fileLabel);
    void trackShortlistEvent("shortlist_export", {
      event_source: "csv_export",
      listing_count: items.length,
      listing_ids: items.map((item) => item.id),
      owner_id: items[0]?.owner_id,
      team_id: items[0]?.team_id,
      shortlist_size: items.length,
    });
  }, [items, trackShortlistEvent]);

  const copyShareLink = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.clipboard) return null;
    await navigator.clipboard.writeText(shareUrl);
    void trackShortlistEvent("shortlist_share", {
      event_source: "copy_link",
      listing_count: items.length,
      listing_ids: items.map((item) => item.id),
      owner_id: items[0]?.owner_id,
      team_id: items[0]?.team_id,
      shortlist_size: items.length,
    });
    return shareUrl;
  }, [items, shareUrl, trackShortlistEvent]);

  const shareNatively = useCallback(async (title = "Signature shortlist") => {
    if (!supportsNativeShare) return false;
    await navigator.share({ title, text: "Review this saved shortlist.", url: shareUrl });
    void trackShortlistEvent("shortlist_share", {
      event_source: "native_share",
      listing_count: items.length,
      listing_ids: items.map((item) => item.id),
      owner_id: items[0]?.owner_id,
      team_id: items[0]?.team_id,
      shortlist_size: items.length,
    });
    return true;
  }, [items, shareUrl, supportsNativeShare, trackShortlistEvent]);

  return {
    clearAll,
    copyShareLink,
    count: ids.length,
    exportCsv,
    ids,
    isAgentShortlistEnabled: isAgentPath,
    isShortlisted,
    items,
    mergeListings,
    removeListing,
    scope,
    shareNatively,
    sharePath,
    shareUrl,
    supportsNativeShare,
    toggleListing,
  };
};